import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Pencil, RotateCw } from 'lucide-react';

import { useAppSelector } from '@/store/hooks';
import { useHaptics } from '@/hooks/useHaptics';
import { BannerBottomSlot, BannerSideSlot } from '@/components/layout/BannerSlots';
import { PlayerIdentity } from '@/components/profile/PlayerIdentity';
import { HatchingEgg } from '@/components/profile/HatchingEgg';
import { EggReward } from '@/components/profile/EggReward';
import { eggForUser, eggNameKey, loadOverride, nextEgg, saveOverride } from '@/lib/eggs';

/**
 * Profilo — chi sei, e le due scelte che ti riguardano.
 *
 * Era la schermata «Impostazioni» e conteneva tutto: banner, avatar, tornei,
 * l'elenco delle preferenze e il logout. Un contenitore senza un tema, in cui
 * ogni cosa era sotto qualcos'altro.
 *
 * Ora porta due comandi primari affiancati. Le impostazioni vivono dietro il
 * secondo, che è dove si va di proposito; il cambio gioco è salito a comando
 * primario perché è un gesto che si RIPETE — sepolto in fondo a un elenco
 * costava tre tocchi e una scorsa ogni volta.
 *
 * Sotto resta spazio libero, deliberatamente: è lì che andrà il profilo vero
 * (statistiche, identità di gioco). Meglio vuoto che riempito con quello che
 * c'era prima solo perché c'era.
 */
export function Profile() {
  const { t } = useTranslation('general');
  const haptics = useHaptics();

  // L'uovo è DERIVATO dall'id dell'utente, non sorteggiato né salvato: stesso
  // utente, stesso uovo, su ogni dispositivo e per sempre, senza una riga di
  // backend. Lo stato sta qui e non dentro `HatchingEgg` perché nome e rarità si
  // mostrano fuori dall'uovo — tenendolo dentro, la riga sopra dovrebbe chiedere
  // al figlio cosa sta disegnando.
  const userId = useAppSelector((s) => s.auth.user?.id);
  const [override, setOverride] = useState(loadOverride);
  const egg = override ?? eggForUser(userId);
  const [rewardOpen, setRewardOpen] = useState(false);

  /** Comando di prova: passa alla variante successiva e annuncia il ritrovamento. */
  const cycleEgg = () => {
    const next = nextEgg(egg.id);
    haptics.impact('medium');
    saveOverride(next.id);
    setOverride(next);
    setRewardOpen(true);
  };

  return (
    // pt-2 e non pt-5: i due comandi appartengono al banner sopra — sono le
    // scelte di QUEL profilo — e uno stacco ampio li faceva leggere come una
    // sezione a sé. Non pt-0 però: attaccati sembrerebbero parte della chrome.
    // h-full + flex: l'avatar deve poter occupare lo spazio che avanza, e il
    // tasto restare in fondo. Senza altezza piena la colonna si stringe al
    // contenuto e l'uovo resta minuscolo in cima.
    // `pack-scope`: sul Profilo la tavolozza diventa cuoio, e i token cambiano
    // per tutto quello che sta qui dentro — targhetta della rarità compresa.
    // L'accento teal non ha più niente da dire su questa schermata: non è una
    // sezione dell'app, è il tuo zaino.
    // `pt-5` e non `pt-2`: da quando la chrome è una targa di cuoio con un
    // bordo netto, il nome dell'uovo ci finiva appiccicato sotto e i due
    // blocchi si leggevano come uno solo. Lo stacco dice che la targa è
    // finita e comincia il contenuto.
    <div className="pack-scope flex h-full flex-col gap-4 px-4 pb-3 pt-5">
      {/* Il tasto di modifica del banner sta ACCANTO al banner, nella chrome,
          non come riga qui sotto. Un comando che modifica una cosa deve stare
          su quella cosa: la riga rettangolare «personalizza banner» costringeva
          a leggere un'etichetta per capire a cosa si riferisse, mentre un tondo
          con la matita al fianco del banner lo dice da solo — ed è il motivo
          per cui il banner, qui e solo qui, si stringe un po' per fargli posto. */}
      <BannerSideSlot>
        <Link
          to="/settings/banner"
          aria-label={t('banner.customize_title', { defaultValue: 'Customize banner' })}
          // Un BOTTONE dello zaino, come quello al centro della cinghia: stesso
          // metallo, stessa ghiera, stessa luce da sopra. Era in accento, ma sul
          // Profilo l'accento non ha più niente da dire — qui siamo dentro un
          // oggetto di cuoio, e le sue parti dure sono di metallo.
          className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full bg-pack-stud text-pack-deep ring-2 ring-[var(--color-pack-stitch)] transition-opacity active:opacity-80 [box-shadow:inset_0_2px_3px_rgba(255,255,255,0.45),inset_0_-3px_4px_rgba(0,0,0,0.30)]"
        >
          <Pencil aria-hidden className="h-5 w-5" strokeWidth={2.5} />
        </Link>
      </BannerSideSlot>

      {/* Nickname e barra dell'esperienza vanno DENTRO la targa di cuoio della
          chrome, non qui in mezzo alle scaglie: sono l'ultima riga del blocco
          che comincia con le valute e passa per lo stendardo. Renderle da qui
          le avrebbe messe sotto la targa, staccate da ciò che descrivono. */}
      <BannerBottomSlot>
        <PlayerIdentity />
      </BannerBottomSlot>

      {/* Nome a sinistra, rarità a destra, sulla stessa riga: sono l'etichetta
          di UN oggetto, e incolonnati si leggerebbero come due informazioni
          distinte. Alle estremità e non affiancati perché la rarità è il dato
          che si cerca a colpo d'occhio — al bordo la si trova senza leggere il
          nome.
          Il `-mt-1` che c'era è stato tolto: serviva a recuperare lo stacco da
          un divisore che non esiste più, e sommato al respiro sotto la targa
          riportava la riga a ridosso della chrome. */}
      <div className="flex items-center justify-between gap-3">
        <span className="flex min-w-0 items-center gap-1.5">
          <span className="min-w-0 truncate text-lg font-black text-fg">
            {t(eggNameKey(egg.id), { defaultValue: egg.id })}
          </span>
          {/* ⚠️ COMANDO DI PROVA. Gira fra le varianti finché non esiste un
              inventario vero, e sparisce con esso. Era un gesto lungo sull'uovo,
              ma dentro Telegram la pressione prolungata su un'immagine la
              intercetta il menu di sistema della WebView: il gesto non arrivava
              mai all'app.
              Piccolo e spento di proposito — deve essere raggiungibile senza
              somigliare a una funzione. */}
          <button
            type="button"
            onClick={cycleEgg}
            aria-label={t('egg_cycle', { defaultValue: 'Change egg (test)' })}
            className="shrink-0 rounded-full p-1 text-fg-muted transition-opacity active:opacity-60"
          >
            <RotateCw aria-hidden className="h-4 w-4" strokeWidth={2.5} />
          </button>
        </span>
        {/* Targhetta piena in accento, non testo colorato: una parola sola
            colorata si legge come un'enfasi, una targhetta si legge come
            un'etichetta applicata all'oggetto — che è quello che la rarità è. */}
        <span className="shrink-0 rounded-full bg-brand px-2.5 py-1 text-[11px] font-black uppercase tracking-wide text-brand-fg">
          {t(`rarity.${egg.rarity}`, { defaultValue: egg.rarity })}
        </span>
      </div>

      {/* L'avatar che deve ancora schiudersi. Si prende lo spazio che avanza e
          si rimpicciolisce sugli schermi bassi, invece di spingere il tasto
          sotto la barra di navigazione. */}
      <HatchingEgg egg={egg} />

      <EggReward egg={egg} open={rewardOpen} onClose={() => setRewardOpen(false)} />
    </div>
  );
}
